import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { CompanySuccessImage } from "@/assets/images";
import { useDataVerificationCompanyEvents } from "@/components/features/ToDos/DataVerification/Company/useDataVerificationCompanyEvents";
import { StandardLayout } from "@/components/layouts";
import { SuccessPage, WizardForm, WizardStep } from "@/components/organisms";
import { policyholderApi, requestApi } from "@/shared/apis";
import { useBreadcrumb, useRouterHook, useToast } from "@/shared/hooks";

import { CompanySteps } from "./constants";
import { companyDataVerificationMapper, companyDefaultValues, CompanyFormType } from "./formType";
import { StyledStandardLayout } from "./styled";
import { getNavigationLabels } from "./utils";

const Company = () => {
    const {
        search: { manager: managerId, partyType },
    } = useRouterHook();
    const { id: policyholderId } = useParams<{
        id: string;
    }>();
    const { t } = useTranslation();
    const { errorToast } = useToast();
    const queryClient = useQueryClient();
    const { setPageTitle } = useBreadcrumb();
    const [showSuccessInfo, setShowSuccessInfo] = useState(false);
    const dataVerificationCompanyEvents = useDataVerificationCompanyEvents();

    const onChangeWizardStep = (wizardStep: WizardStep) => {
        dataVerificationCompanyEvents.DataVerificationCompanyProgressed(wizardStep.stepId);
    };

    const { data: defaultValues, isFetching: isFetchingInfo } = useQuery(
        ["getCompanyDataVerification", { policyholderId }],
        () =>
            policyholderApi.getCompanyDataVerification(policyholderId, {
                partyType: partyType as MyBrokerApi.PartyType,
            }),
        {
            refetchOnWindowFocus: false,
            enabled: !!policyholderId,
        },
    );

    const { mutate: requestCompanyDataVerification } = useMutation(requestApi.requestCompanyDataVerification, {
        onSuccess: () => {
            dataVerificationCompanyEvents.DataVerificationCompanyFinalized(true);
            queryClient.invalidateQueries("requestsPaginated");
            setShowSuccessInfo(true);
        },
        onError: () => {
            dataVerificationCompanyEvents.DataVerificationCompanyFinalized(false);
            errorToast({ message: t("Errors.FormGenericError") });
        },
    });

    useEffect(() => {
        dataVerificationCompanyEvents.DataVerificationCompanyStarted();
        return () => dataVerificationCompanyEvents.DataVerificationCompanyStopped();
    }, []);

    useEffect(() => {
        if (defaultValues) {
            setPageTitle(defaultValues?.general?.name);
        }
    }, [defaultValues]);

    const handleSubmitCompany = (companyDataVerification: CompanyFormType) => {
        requestCompanyDataVerification(companyDataVerificationMapper(companyDataVerification));
    };

    if (showSuccessInfo) {
        return (
            <StandardLayout title={t("PageTitles.DataVerificationCompany")}>
                <SuccessPage icon={<CompanySuccessImage />} message={t("DataVerification.Company.InfoSuccess")} />
            </StandardLayout>
        );
    }

    return (
        <StyledStandardLayout title={t("PageTitles.DataVerificationCompany")} disableSearch>
            {!isFetchingInfo && (
                <WizardForm
                    condensed
                    wizardSteps={CompanySteps}
                    defaultFormValues={companyDefaultValues(defaultValues, managerId)}
                    handleSubmit={handleSubmitCompany}
                    getNavigationLabels={getNavigationLabels}
                    onChangeWizardStep={onChangeWizardStep}
                />
            )}
        </StyledStandardLayout>
    );
};

export default Company;
